import { FabricImage, type FabricObject } from 'fabric';
import { fitImage, TRANSPARENT_PIXEL, type ImageFit } from '@doer/frontend-components/creativity-editor';
import type { Effect } from './effect';

type Bbox = { x: number; y: number; w: number; h: number };

type ImageValue = string | { src: string; bbox?: Bbox };

function isBbox(v: unknown): v is Bbox {
  if (v == null || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.x === 'number' &&
    typeof o.y === 'number' &&
    typeof o.w === 'number' &&
    typeof o.h === 'number' &&
    o.w > 0 &&
    o.h > 0
  );
}

// Returns: parsed value to apply, '' for explicit clear, or null for skip.
function parse(value: unknown): ImageValue | '' | null {
  if (value === '') return '';
  if (typeof value === 'string') return value;
  if (value != null && typeof value === 'object') {
    const { src, bbox } = value as { src?: unknown; bbox?: unknown };
    if (typeof src === 'string' && src) return { src, bbox: isBbox(bbox) ? bbox : undefined };
  }
  return null;
}

// No usamos `instanceof FabricImage`: en dev, un HMR o dos copias de fabric en
// el bundle rompen la identidad de la clase y el instanceof da false aunque el
// objeto SEA una imagen (type === 'image'). Chequeamos por capacidad.
function isImagePiece(obj: FabricObject): obj is FabricImage {
  return obj.type === 'image' && typeof (obj as FabricImage).setSrc === 'function';
}

function target(obj: FabricImage, bbox?: Bbox): { width: number; height: number } {
  if (bbox) return { width: bbox.w, height: bbox.h };
  const declared = obj.imageBox;
  if (declared && declared.width > 0 && declared.height > 0) return { width: declared.width, height: declared.height };
  return { width: (obj.width ?? 0) * (obj.scaleX ?? 1), height: (obj.height ?? 0) * (obj.scaleY ?? 1) };
}

export const imageEffect: Effect = {
  applyToJson(obj, value) {
    const v = parse(value);
    if (v === null) return null;
    if (v === '') return { ...obj, src: TRANSPARENT_PIXEL };
    if (typeof v === 'string') return { ...obj, src: v };
    return {
      ...obj,
      src: v.src,
      left: v.bbox?.x ?? obj.left,
      top: v.bbox?.y ?? obj.top,
      imageBox: v.bbox ? { width: v.bbox.w, height: v.bbox.h } : obj.imageBox
    };
  },

  async applyToCanvas(obj, value) {
    const v = parse(value);
    if (v === null) return;

    if (!isImagePiece(obj)) {
      console.warn(
        `[image-effect] image-replace targets non-Image piece (pieceId=${obj.pieceId}, type=${obj.type}). Skipping.`
      );
      return;
    }

    if (v === '') {
      await obj.setSrc(TRANSPARENT_PIXEL, { crossOrigin: 'anonymous' });
      const box = target(obj);
      if (obj.imageFit && obj.imageFit !== 'none' && box.width > 0 && box.height > 0) {
        fitImage(obj, obj.imageFit, box);
      }
      // Notify so the editor's model/history capture this change (setSrc emits no event).
      obj.canvas?.fire('object:modified', { target: obj });
      return;
    }

    const src = typeof v === 'string' ? v : v.src;
    const bbox = typeof v === 'string' ? undefined : v.bbox;

    const fit: ImageFit | undefined = obj.imageFit;
    const box = target(obj, bbox);

    if (bbox) {
      obj.set({ left: bbox.x, top: bbox.y });
      obj.imageBox = { width: bbox.w, height: bbox.h };
    }

    await obj.setSrc(src, { crossOrigin: 'anonymous' });

    if (fit && fit !== 'none' && box.width > 0 && box.height > 0) {
      fitImage(obj, fit, box);
    }

    // Notify so the editor's model/history capture this change (setSrc emits no event).
    obj.canvas?.fire('object:modified', { target: obj });
  }
};
